import { X } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { splitMemberFullName } from '../api/membersDirectory';

/**
 * @param {string | null | undefined} iso
 */
function isoToDateInputValue(iso) {
    if (!iso || typeof iso !== 'string') return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

/**
 * @param {string} dateStr
 */
function dateInputToIso(dateStr) {
    if (!dateStr.trim()) return '';
    return new Date(`${dateStr}T12:00:00`).toISOString();
}

/**
 * @param {ReturnType<import('../api/membersDirectory').mapApiSpouseToView>} spouse
 */
function spouseToFormState(spouse) {
    let firstName = String(spouse.firstName || '').trim();
    let lastName = String(spouse.lastName || '').trim();
    if (!firstName && !lastName) {
        const split = splitMemberFullName(spouse.fullName);
        firstName = split.givenName;
        lastName = split.familyName;
    }

    return {
        firstName,
        lastName,
        phoneNumber: String(spouse.phoneNumber || ''),
        email: String(spouse.email || ''),
        jobTitle: String(spouse.jobTitle || ''),
        company: String(spouse.company || ''),
        sector: String(spouse.sector || ''),
        university: String(spouse.university || ''),
        highSchool: String(spouse.highSchool || ''),
        address: String(spouse.address || ''),
        tcKimlikNo: String(spouse.tcKimlikNo || ''),
        birthDate: isoToDateInputValue(spouse.birthDate),
    };
}

const EMPTY_FORM = spouseToFormState({
    id: '',
    fullName: '',
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    jobTitle: '',
    company: '',
    sector: '',
    university: '',
    highSchool: '',
    address: '',
    tcKimlikNo: '',
    birthDate: null,
    profilePictureUrl: '',
});

/**
 * @param {{
 *   id: string;
 *   label: string;
 *   value: string;
 *   onChange: (v: string) => void;
 *   type?: string;
 *   disabled?: boolean;
 *   required?: boolean;
 *   error?: string;
 * }} props
 */
function Field({
    id,
    label,
    value,
    onChange,
    type = 'text',
    disabled,
    required = false,
    error = '',
}) {
    return (
        <div className="mb-3">
            <label htmlFor={id} className="form-label login-field-label">
                {label}
                {required ? (
                    <span className="text-danger ms-1" aria-hidden="true">
                        *
                    </span>
                ) : null}
            </label>
            <input
                id={id}
                type={type}
                className={`form-control login-field-input${error ? ' is-invalid' : ''}`}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                disabled={disabled}
                aria-invalid={error ? 'true' : undefined}
                aria-describedby={error ? `${id}-error` : undefined}
            />
            {error ? (
                <div
                    id={`${id}-error`}
                    className="invalid-feedback d-block"
                    role="alert"
                >
                    {error}
                </div>
            ) : null}
        </div>
    );
}

/**
 * @param {{
 *   open: boolean;
 *   onClose: () => void;
 *   onSubmit: (
 *     subMemberId: string,
 *     payload: Record<string, string>,
 *   ) => void | Promise<void>;
 *   submitting?: boolean;
 *   spouse: ReturnType<import('../api/membersDirectory').mapApiSpouseToView> | null;
 * }} props
 */
export function EditSpouseModal({
    open,
    onClose,
    onSubmit,
    submitting = false,
    spouse,
}) {
    const { t } = useTranslation();
    const titleId = useId();
    const [form, setForm] = useState(EMPTY_FORM);
    const [error, setError] = useState('');
    const [fieldErrors, setFieldErrors] = useState(
        /** @type {Record<string, string>} */ ({}),
    );

    useEffect(() => {
        if (!open || !spouse) {
            setForm(EMPTY_FORM);
            setError('');
            setFieldErrors({});
            return;
        }
        setForm(spouseToFormState(spouse));
        setError('');
        setFieldErrors({});
    }, [open, spouse]);

    useEffect(() => {
        if (!open || Object.keys(fieldErrors).length === 0) return;
        document
            .querySelector('.app-modal .is-invalid')
            ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, [fieldErrors, open]);

    useEffect(() => {
        if (!open) return undefined;

        function onKeyDown(e) {
            if (e.key === 'Escape' && !submitting) {
                onClose();
            }
        }

        document.addEventListener('keydown', onKeyDown);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.body.style.overflow = prevOverflow;
        };
    }, [open, onClose, submitting]);

    if (!open || !spouse?.id) return null;

    function setField(key, value) {
        setForm((prev) => ({ ...prev, [key]: value }));
        setFieldErrors((prev) => {
            if (!prev[key]) return prev;
            const next = { ...prev };
            delete next[key];
            return next;
        });
    }

    async function handleSubmit(e) {
        e.preventDefault();
        setError('');

        const firstName = form.firstName.trim();
        const lastName = form.lastName.trim();
        const nextFieldErrors = /** @type {Record<string, string>} */ ({});
        const requiredMessage = t('profilePage.fieldRequired');

        if (!firstName) {
            nextFieldErrors.firstName = requiredMessage;
        }
        if (!lastName) {
            nextFieldErrors.lastName = requiredMessage;
        }

        if (Object.keys(nextFieldErrors).length > 0) {
            setFieldErrors(nextFieldErrors);
            return;
        }

        setFieldErrors({});

        const payload = {
            firstName,
            lastName,
            phoneNumber: form.phoneNumber.trim(),
            email: form.email.trim(),
            jobTitle: form.jobTitle.trim(),
            company: form.company.trim(),
            sector: form.sector.trim(),
            university: form.university.trim(),
            highSchool: form.highSchool.trim(),
            address: form.address.trim(),
            tcKimlikNo: form.tcKimlikNo.trim(),
            birthDate: dateInputToIso(form.birthDate),
        };

        try {
            await onSubmit(String(spouse.id), payload);
        } catch (err) {
            const msg =
                err && typeof err === 'object' && 'message' in err
                    ? String(/** @type {{ message?: unknown }} */ (err).message)
                    : '';
            setError(msg || t('profilePage.editSpouseError'));
        }
    }

    return (
        <div
            className="app-modal-backdrop"
            onClick={submitting ? undefined : onClose}
            role="presentation"
        >
            <div
                className="app-modal app-modal--wide"
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="app-modal-header">
                    <h2 id={titleId} className="app-modal-title h6 mb-0">
                        {t('profilePage.editSpouseTitle')}
                    </h2>
                    <button
                        type="button"
                        className="app-modal-close btn btn-link p-1"
                        onClick={onClose}
                        disabled={submitting}
                        aria-label={t('profilePage.addChildClose')}
                    >
                        <X size={20} strokeWidth={2} aria-hidden />
                    </button>
                </div>
                <form onSubmit={handleSubmit} noValidate>
                    <div className="app-modal-body app-modal-body--scroll">
                        <p className="edit-spouse-modal-section-label small text-secondary mb-2">
                            {t('profilePage.editSpouseSectionPersonal')}
                        </p>
                        <div className="row g-3">
                            <div className="col-sm-6">
                                <Field
                                    id="edit-spouse-first-name"
                                    label={t('profilePage.addChildFirstName')}
                                    value={form.firstName}
                                    onChange={(v) => setField('firstName', v)}
                                    disabled={submitting}
                                    required
                                    error={fieldErrors.firstName}
                                />
                            </div>
                            <div className="col-sm-6">
                                <Field
                                    id="edit-spouse-last-name"
                                    label={t('profilePage.addChildLastName')}
                                    value={form.lastName}
                                    onChange={(v) => setField('lastName', v)}
                                    disabled={submitting}
                                    required
                                    error={fieldErrors.lastName}
                                />
                            </div>
                            <div className="col-sm-6">
                                <Field
                                    id="edit-spouse-birth-date"
                                    label={t('profilePage.addChildBirthDate')}
                                    type="date"
                                    value={form.birthDate}
                                    onChange={(v) => setField('birthDate', v)}
                                    disabled={submitting}
                                />
                            </div>
                            <div className="col-sm-6">
                                <Field
                                    id="edit-spouse-tc"
                                    label={t('profilePage.editSpouseTcKimlikNo')}
                                    value={form.tcKimlikNo}
                                    onChange={(v) => setField('tcKimlikNo', v)}
                                    disabled={submitting}
                                />
                            </div>
                            <div className="col-sm-6">
                                <Field
                                    id="edit-spouse-phone"
                                    label={t('profilePage.editSpousePhone')}
                                    value={form.phoneNumber}
                                    onChange={(v) => setField('phoneNumber', v)}
                                    disabled={submitting}
                                />
                            </div>
                            <div className="col-sm-6">
                                <Field
                                    id="edit-spouse-email"
                                    label={t('profilePage.editSpouseEmail')}
                                    type="email"
                                    value={form.email}
                                    onChange={(v) => setField('email', v)}
                                    disabled={submitting}
                                />
                            </div>
                            <div className="col-12">
                                <Field
                                    id="edit-spouse-address"
                                    label={t('profilePage.editSpouseAddress')}
                                    value={form.address}
                                    onChange={(v) => setField('address', v)}
                                    disabled={submitting}
                                />
                            </div>
                        </div>

                        <p className="edit-spouse-modal-section-label small text-secondary mb-2 mt-2">
                            {t('profilePage.editSpouseSectionWork')}
                        </p>
                        <div className="row g-3">
                            <div className="col-sm-6">
                                <Field
                                    id="edit-spouse-job-title"
                                    label={t('profilePage.editSpouseJobTitle')}
                                    value={form.jobTitle}
                                    onChange={(v) => setField('jobTitle', v)}
                                    disabled={submitting}
                                />
                            </div>
                            <div className="col-sm-6">
                                <Field
                                    id="edit-spouse-company"
                                    label={t('profilePage.editSpouseCompany')}
                                    value={form.company}
                                    onChange={(v) => setField('company', v)}
                                    disabled={submitting}
                                />
                            </div>
                            <div className="col-sm-6">
                                <Field
                                    id="edit-spouse-sector"
                                    label={t('profilePage.editSpouseSector')}
                                    value={form.sector}
                                    onChange={(v) => setField('sector', v)}
                                    disabled={submitting}
                                />
                            </div>
                        </div>

                        <p className="edit-spouse-modal-section-label small text-secondary mb-2 mt-2">
                            {t('profilePage.editSpouseSectionEducation')}
                        </p>
                        <div className="row g-3">
                            <div className="col-sm-6">
                                <Field
                                    id="edit-spouse-university"
                                    label={t('profilePage.editSpouseUniversity')}
                                    value={form.university}
                                    onChange={(v) => setField('university', v)}
                                    disabled={submitting}
                                />
                            </div>
                            <div className="col-sm-6">
                                <Field
                                    id="edit-spouse-high-school"
                                    label={t('profilePage.editSpouseHighSchool')}
                                    value={form.highSchool}
                                    onChange={(v) => setField('highSchool', v)}
                                    disabled={submitting}
                                />
                            </div>
                        </div>

                        {error ? (
                            <p
                                className="app-modal-error small text-danger mb-0 mt-3"
                                role="alert"
                            >
                                {error}
                            </p>
                        ) : null}
                    </div>
                    <div className="app-modal-footer">
                        <button
                            type="button"
                            className="btn btn-outline-secondary"
                            onClick={onClose}
                            disabled={submitting}
                        >
                            {t('profilePage.addChildCancel')}
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={submitting}
                        >
                            {submitting
                                ? t('profilePage.editSpouseSubmitting')
                                : t('profilePage.editSpouseSubmit')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
